import { useEffect, useState } from "react";
import {
  Container,
  Form,
  Button,
  Modal,
  Table,
  Card,
  Badge,
} from "react-bootstrap";
import { toast } from "react-toastify";
import Sample14 from "../Components/Logo";

import { getAllDepartmentList } from "../Api/DepartmentList";
import {
  createDepartmentOrg,
  getDepartmentsByOrgId,
} from "../Api/DepartmentOrgMaster";

const DepartmentList = () => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [departmentList, setDepartmentList] = useState([]);
  const [orgDepartments, setOrgDepartments] = useState([]);

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const orgId = String(userDetails?.organisationId);

  // 🔑 CHECK FOR RESTRICTED ORG
  const isRestrictedOrg = orgId === "2001";

  const [deptData, setDeptData] = useState({
    deptCode: "",
    deptDescription: "",
  });

  // ================= FETCH MASTER =================
  const fetchDepartmentMaster = async () => {
    try {
      setLoading(true);
      const data = await getAllDepartmentList();
      setDepartmentList(data || []);
    } catch {
      toast.error("Failed to load department master");
    } finally {
      setLoading(false);
    }
  };

  // ================= FETCH ORG =================
  const fetchOrgDepartments = async () => {
    if (isRestrictedOrg) return; // ⛔ Skip API call

    try {
      setLoading(true);
      const data = await getDepartmentsByOrgId(orgId);
      setOrgDepartments(data || []);
    } catch {
      toast.error("Failed to load organization departments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartmentMaster();
    fetchOrgDepartments();
  }, []);

  // ================= SAVE =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      await createDepartmentOrg({
        deptCode: deptData.deptCode,
        deptDescription: deptData.deptDescription,
        orgId,
      });

      toast.success("Department successfully added");

      setShowModal(false);
      setDeptData({ deptCode: "", deptDescription: "" });
      fetchOrgDepartments();
    } catch {
      toast.error("Failed to assign department");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="main-content expand">
        <Container fluid>
          {/* ================= HEADER ================= */}
          <Card className="mb-4 shadow-sm border-0">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="fw-bold mb-1">🏢 Organization Departments</h4>
                <small className="text-muted">
                  Manage department mapping for this organization
                </small>
              </div>

              {/* 🔒 Hide Add button for org 2001 */}
              {!isRestrictedOrg && (
                <Button
                  variant="primary"
                  className="px-4"
                  onClick={() => setShowModal(true)}
                >
                  + Add Department
                </Button>
              )}
            </Card.Body>
          </Card>

          {/* ================= TABLE ================= */}
          {!isRestrictedOrg ? (
            <Card className="shadow-sm border-0">
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Dept Org ID</th>
                      <th>Department Code</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgDepartments.length > 0 ? (
                      orgDepartments.map((dept, index) => (
                        <tr key={dept.deptOrgId}>
                          <td>{index + 1}</td>
                          <td>
                            <Badge bg="secondary">{dept.deptOrgId}</Badge>
                          </td>
                          <td>
                            <Badge bg="info">{dept.deptCode}</Badge>
                          </td>
                          <td>{dept.deptDescription}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-5">
                          <div className="text-muted">
                            <h6>No departments assigned</h6>
                            <p className="mb-0">
                              Click <b>Add Department</b> to assign one
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          ) : (
            // 🔒 Message for org 2001
            <Card className="shadow-sm border-0">
              <Card.Body className="text-center py-5">
                <h6 className="text-muted">
                  Department mapping is not applicable for this organization.
                </h6>
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>

      {/* ================= MODAL ================= */}
      {!isRestrictedOrg && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Header closeButton className="border-0 pb-0">
            <div>
              <Modal.Title className="fw-bold">➕ Add Department</Modal.Title>
              <small className="text-muted">
                Assign a department to this organization
              </small>
            </div>
          </Modal.Header>

          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">
                      Select Department
                    </Form.Label>
                    <Form.Select
                      value={deptData.deptCode}
                      onChange={(e) => {
                        const selected = departmentList.find(
                          (d) => d.deptCode === e.target.value,
                        );

                        setDeptData({
                          deptCode: selected?.deptCode || "",
                          deptDescription: selected?.deptDescription || "",
                        });
                      }}
                      required
                    >
                      <option value="">-- Select Department --</option>
                      {departmentList.map((dept) => (
                        <option key={dept.deptGuid} value={dept.deptCode}>
                          {dept.deptCode} - {dept.deptDescription}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="fw-medium">
                      Department Description
                    </Form.Label>
                    <Form.Control value={deptData.deptDescription} readOnly />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Modal.Body>

            <Modal.Footer className="border-0">
              <Button
                variant="outline-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save Department
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      {/* ================= LOADER ================= */}
      {loading && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}
    </>
  );
};

export default DepartmentList;

// import { useEffect, useState } from "react";
// import {
//   Container,
//   Form,
//   Button,
//   Modal,
//   Table,
//   Card,
//   Badge,
//   Spinner,
// } from "react-bootstrap";
// import { toast } from "react-toastify";
// import Sample14 from "../Components/Logo";

// import { getAllDepartmentList } from "../Api/DepartmentList";
// import {
//   createDepartmentOrg,
//   getDepartmentsByOrgId,
// } from "../Api/DepartmentOrgMaster";

// const DepartmentList = () => {
//   const [loading, setLoading] = useState(false);
//   const [showModal, setShowModal] = useState(false);

//   const [departmentList, setDepartmentList] = useState([]);
//   const [orgDepartments, setOrgDepartments] = useState([]);

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const orgId = String(userDetails?.organisationId);

//   const [deptData, setDeptData] = useState({
//     deptCode: "",
//     deptDescription: "",
//   });

//   // ================= FETCH MASTER =================
//   const fetchDepartmentMaster = async () => {
//     try {
//       setLoading(true);
//       const data = await getAllDepartmentList();
//       setDepartmentList(data || []);
//     } catch {
//       toast.error("Failed to load department master");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ================= FETCH ORG =================
//   const fetchOrgDepartments = async () => {
//     try {
//       setLoading(true);
//       const data = await getDepartmentsByOrgId(orgId);
//       setOrgDepartments(data || []);
//     } catch {
//       toast.error("Failed to load organization departments");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchDepartmentMaster();
//     fetchOrgDepartments();
//   }, []);

//   // ================= SAVE =================
//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     try {
//       setLoading(true);

//       await createDepartmentOrg({
//         deptCode: deptData.deptCode,
//         deptDescription: deptData.deptDescription,
//         orgId,
//       });

//       toast.success("Department successfully added");

//       setShowModal(false);
//       setDeptData({ deptCode: "", deptDescription: "" });
//       fetchOrgDepartments();
//     } catch {
//       toast.error("Failed to assign department");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <>
//       <div className="main-content expand">
//         <Container fluid>
//           {/* ================= HEADER ================= */}
//           <Card className="mb-4 shadow-sm border-0">
//             <Card.Body className="d-flex justify-content-between align-items-center">
//               <div>
//                 <h4 className="fw-bold mb-1">🏢 Organization Departments</h4>
//                 <small className="text-muted">
//                   Manage department mapping for this organization
//                 </small>
//               </div>
//               <Button
//                 variant="primary"
//                 className="px-4"
//                 onClick={() => setShowModal(true)}
//               >
//                 + Add Department
//               </Button>
//             </Card.Body>
//           </Card>

//           {/* ================= TABLE ================= */}
//           <Card className="shadow-sm border-0">
//             <Card.Body className="p-0">
//               <Table hover responsive className="mb-0 align-middle">
//                 <thead className="table-light">
//                   <tr>
//                     <th>#</th>
//                     <th>Dept Org ID</th>
//                     <th>Department Code</th>
//                     <th>Description</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {orgDepartments.length > 0 ? (
//                     orgDepartments.map((dept, index) => (
//                       <tr key={dept.deptOrgId}>
//                         <td>{index + 1}</td>
//                         <td>
//                           <Badge bg="secondary">{dept.deptOrgId}</Badge>
//                         </td>
//                         <td>
//                           <Badge bg="info">{dept.deptCode}</Badge>
//                         </td>
//                         <td>{dept.deptDescription}</td>
//                       </tr>
//                     ))
//                   ) : (
//                     <tr>
//                       <td colSpan="4" className="text-center py-5">
//                         <div className="text-muted">
//                           <h6>No departments assigned</h6>
//                           <p className="mb-0">
//                             Click <b>Add Department</b> to assign one
//                           </p>
//                         </div>
//                       </td>
//                     </tr>
//                   )}
//                 </tbody>
//               </Table>
//             </Card.Body>
//           </Card>
//         </Container>
//       </div>

//       {/* ================= MODAL ================= */}
//       <Modal show={showModal} onHide={() => setShowModal(false)} centered>
//         <Modal.Header closeButton className="border-0 pb-0">
//           <div>
//             <Modal.Title className="fw-bold">➕ Add Department</Modal.Title>
//             <small className="text-muted">
//               Assign a department to this organization
//             </small>
//           </div>
//         </Modal.Header>

//         <Form onSubmit={handleSubmit}>
//           <Modal.Body>
//             <Card className="border-0 shadow-sm">
//               <Card.Body>
//                 <Form.Group className="mb-3">
//                   <Form.Label className="fw-medium">
//                     Select Department
//                   </Form.Label>
//                   <Form.Select
//                     value={deptData.deptCode}
//                     onChange={(e) => {
//                       const selected = departmentList.find(
//                         (d) => d.deptCode === e.target.value
//                       );

//                       setDeptData({
//                         deptCode: selected?.deptCode || "",
//                         deptDescription: selected?.deptDescription || "",
//                       });
//                     }}
//                     required
//                   >
//                     <option value="">-- Select Department --</option>
//                     {departmentList.map((dept) => (
//                       <option key={dept.deptGuid} value={dept.deptCode}>
//                         {dept.deptCode} - {dept.deptDescription}
//                       </option>
//                     ))}
//                   </Form.Select>
//                 </Form.Group>

//                 <Form.Group>
//                   <Form.Label className="fw-medium">
//                     Department Description
//                   </Form.Label>
//                   <Form.Control value={deptData.deptDescription} readOnly />
//                 </Form.Group>
//               </Card.Body>
//             </Card>
//           </Modal.Body>

//           <Modal.Footer className="border-0">
//             <Button
//               variant="outline-secondary"
//               onClick={() => setShowModal(false)}
//             >
//               Cancel
//             </Button>
//             <Button variant="primary" type="submit">
//               Save Department
//             </Button>
//           </Modal.Footer>
//         </Form>
//       </Modal>

//       {/* ================= LOADER ================= */}
//       {loading && (
//         <div className="loading-overlay">
//           <Sample14 />
//         </div>
//       )}
//     </>
//   );
// };

// export default DepartmentList;
